import { Component, ReactNode } from "react";
import { MfeErrorState } from "./MfeErrorState";

interface MfeErrorBoundaryProps {
  name: string;
  children: ReactNode;
  onRetry?: () => void;
}

interface MfeErrorBoundaryState {
  hasError: boolean;
  error: Error | null;
}

/**
 * Per-MFE Error Boundary.
 *
 * Catches runtime rendering errors INSIDE a mounted MFE so they don't propagate
 * to the app-level ErrorBoundary and crash the entire shell + other MFEs.
 *
 * This only catches errors during React rendering/lifecycle. Load errors
 * (network failures fetching remoteEntry.js) are handled separately by
 * MfeLoader's try/catch in loadMfe().
 */
export class MfeErrorBoundary extends Component<
  MfeErrorBoundaryProps,
  MfeErrorBoundaryState
> {
  constructor(props: MfeErrorBoundaryProps) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error: Error): MfeErrorBoundaryState {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
    console.error(
      `[MfeErrorBoundary] Runtime error in MFE "${this.props.name}":`,
      error,
      errorInfo.componentStack,
    );
    // TODO: Report to Dynatrace / observability service
  }

  componentDidUpdate(prevProps: MfeErrorBoundaryProps) {
    // Reset error state when the MFE name changes (navigating to a different MFE)
    if (prevProps.name !== this.props.name && this.state.hasError) {
      this.setState({ hasError: false, error: null });
    }
  }

  handleRetry = () => {
    this.setState({ hasError: false, error: null });
    this.props.onRetry?.();
  };

  handleGoHome = () => {
    // Full page refresh to home — clears all state, re-initializes shell
    window.location.href = "/";
  };

  render() {
    if (this.state.hasError) {
      return (
        <MfeErrorState
          mfeName={this.props.name}
          error={this.state.error}
          onRetry={this.handleRetry}
          onGoHome={this.handleGoHome}
        />
      );
    }

    return this.props.children;
  }
}

export default MfeErrorBoundary;
